import * as RadixPopover from '@radix-ui/react-popover';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import { type AnimationEvent, memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { usePrevious } from 'react-use';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';
import { Command } from '@air/cmdk';

import { SearchCommandActions } from '~/components/SearchCommand/SearchCommandActions';
import {
  SearchCommandClearButton,
  type SearchCommandClearButtonProps,
} from '~/components/SearchCommand/SearchCommandClearButton';
import { SearchCommandInputBase } from '~/components/SearchCommand/SearchCommandInputBase';
import { SearchCommandList } from '~/components/SearchCommand/SearchCommandList';
import { SearchCommandTrigger, wrapSearchCommandOpenButton } from '~/components/SearchCommand/SearchCommandTrigger';
import { SEARCH_COMMAND_VISUAL_SEARCH_TOOLTIP } from '~/components/SearchCommand/SearchCommandVisualSearchButton';
import { SearchCommandVisualSearchPanel } from '~/components/SearchCommand/SearchCommandVisualSearchPanel';
import { SmartSearchCommandList } from '~/components/SearchCommand/SmartSearchCommandList';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { SEARCH_COMMAND_OPEN_HOTKEYS } from '~/utils/KeyUtils';
import { setHasSeenSmartSearchPromotion } from '~/utils/SearchUtils';

const SEARCH_INPUT_FOCUS_DELAY_MS = 50;

export type SearchCommandRenderActionsProps = {
  onClearSearchValue: () => void;
  onSetSearchValue: (value: string) => void;
};

export type SearchCommandListStartHelpers = {
  onClose: () => void;
};

export type SearchCommandProps = {
  compact?: boolean;
  defaultSearch?: string;
  filterBar?: ReactNode;
  filtersCount?: number;
  hideSuggestions?: boolean;
  onBoardClick?: (board: SearchBoardsResponse) => void;
  onOpenChange: (isOpen: boolean) => void;
  onSavedFilterSelect?: (savedFilter: SearchSavedFiltersResponse) => void;
  onSearchChange?: (search: string) => void;
  onSuggestionClick?: (suggestion: string) => void;
  placeholder?: string;
  suggestions?: SuggestionsResponse;
  onClear?: SearchCommandClearButtonProps['onClick'];
  renderActions?: (props: SearchCommandRenderActionsProps) => ReactNode;
  renderCustomContent?: () => ReactNode;
  renderListStart?: (helpers: SearchCommandListStartHelpers) => ReactNode;
  search?: string;
  onClearSimilarToSearch?: () => void;
  onSimilarImageSearch?: (base64Image: string) => void;
};

export const SearchCommand = memo(
  ({
    compact,
    filterBar,
    filtersCount,
    hideSuggestions = false,
    onBoardClick: _onBoardClick,
    onClear,
    onOpenChange,
    onSavedFilterSelect: _onSavedFilterSelect,
    onSearchChange,
    onSuggestionClick: _onSuggestionClick,
    onClearSimilarToSearch,
    onSimilarImageSearch: _onSimilarImageSearch,
    placeholder,
    renderActions,
    renderCustomContent,
    renderListStart,
    search,
    suggestions,
  }: SearchCommandProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const { searchType, canUseSmartSearch } = usePrivateSearchContext();
    const hasClosedEndActions = !!renderActions || (!!onClear && !!searchValue);
    const hasOpenEndActions = hasClosedEndActions || (!!onClear && isOpen);

    const prevSearchType = usePrevious(searchType);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const openAsVisualSearchRef = useRef<boolean>();
    const showVisualSearchPanel = isVisualSearchOpen;

    useEffect(() => {
      // We want to make sure that the value is set to an empty string instead of undefined
      // to avoid the input defaulting on the `defaultValue` prop.
      setSearchValue(search ?? '');
    }, [search]);

    useEffect(() => {
      onOpenChange(isOpen);
    }, [isOpen, onOpenChange]);

    const handleOpen = useCallback(() => {
      openAsVisualSearchRef.current = false;
      setIsVisualSearchOpen(false);
      setIsOpen(true);
    }, []);

    const handleCommandOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
      if (!open) {
        openAsVisualSearchRef.current = false;
        return;
      }

      if (openAsVisualSearchRef.current) {
        openAsVisualSearchRef.current = false;
        return;
      }

      setIsVisualSearchOpen(false);
    }, []);

    const handleVisualSearchClick = useCallback(() => {
      openAsVisualSearchRef.current = true;
      setIsVisualSearchOpen(true);
      setIsOpen(true);
    }, []);

    const handleCloseVisualSearch = useCallback(() => {
      // Leave `isVisualSearchOpen` true until the popover close animation ends so
      // keyword suggestions do not mount for a frame while the panel is fading out.
      setIsOpen(false);
    }, []);

    const handleSearchCommandContentAnimationEnd = useCallback(
      (event: AnimationEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || isOpen) {
          return;
        }

        setIsVisualSearchOpen(false);
      },
      [isOpen],
    );

    useEffect(() => {
      if (canUseSmartSearch && !isUndefined(prevSearchType) && prevSearchType !== searchType) {
        if (isUndefined(search) && searchType === 'keyword') {
          setIsOpen(true);
        }

        // NOTE: Workaround for race condition when switching between Popover trigger and search input.
        // The search input ref needs time to become available after the state change before we can
        // call blur() or focus() on it, so we add a 50ms delay to ensure the ref is ready.
        window.setTimeout(() => {
          if (search) {
            searchInputRef.current?.blur();
          } else {
            searchInputRef.current?.focus();
          }
        }, SEARCH_INPUT_FOCUS_DELAY_MS);
      }
    }, [canUseSmartSearch, prevSearchType, searchType, search]);

    const onBoardClick = useCallback(
      (board: SearchBoardsResponse) => {
        setSearchValue('');
        onSearchChange?.('');
        setIsOpen(false);
        _onBoardClick?.(board);
      },
      [_onBoardClick, onSearchChange],
    );

    const onSavedFilterSelect = useCallback(
      (savedFilter: SearchSavedFiltersResponse) => {
        setIsOpen(false);
        _onSavedFilterSelect?.(savedFilter);
      },
      [_onSavedFilterSelect],
    );

    const onSuggestionClick = useCallback(
      (suggestion: string) => {
        setIsOpen(false);
        setSearchValue(suggestion);
        _onSuggestionClick?.(suggestion);
      },
      [_onSuggestionClick],
    );

    const handleSimilarImageSearch = useCallback(
      (base64Image: string) => {
        setIsOpen(false);
        setSearchValue('');
        _onSimilarImageSearch?.(base64Image);
      },
      [_onSimilarImageSearch],
    );

    const onValueChange = useCallback(
      (value: string) => {
        if (value) {
          onClearSimilarToSearch?.();
        }
        setSearchValue(value);
        onSearchChange?.(value);
        if (canUseSmartSearch) {
          setHasSeenSmartSearchPromotion(true);
        }
      },
      [canUseSmartSearch, onClearSimilarToSearch, onSearchChange],
    );

    const handleSetSearchValue = useCallback(
      (value: string) => {
        setSearchValue(value);
        onSearchChange?.(value);
      },
      [onSearchChange],
    );

    const handleClearSearchValue = useCallback(() => {
      handleSetSearchValue('');
    }, [handleSetSearchValue]);

    const handleCloseSearchCommand = useCallback(() => {
      setSearchValue('');
      onSearchChange?.('');
      setIsOpen(false);
    }, [onSearchChange]);

    useHotkeys(SEARCH_COMMAND_OPEN_HOTKEYS, handleOpen, {
      enableOnFormTags: true,
      preventDefault: true,
    });

    useHotkeys('esc', handleCloseVisualSearch, {
      enabled: isVisualSearchOpen && isOpen,
      enableOnFormTags: true,
      preventDefault: true,
    });

    return (
      <Command className="w-full" data-testid="SEARCH_COMMAND" shouldFilter={false}>
        <RadixPopover.Root onOpenChange={handleCommandOpenChange} open={isOpen}>
          <div className="relative">
            <RadixPopover.Anchor asChild>
              <SearchCommandTrigger
                compact={compact}
                count={filtersCount}
                onVisualSearchClick={handleVisualSearchClick}
                placeholder={placeholder}
                search={searchValue}
                visualSearchTooltip={SEARCH_COMMAND_VISUAL_SEARCH_TOOLTIP}
                wrapOpenButton={wrapSearchCommandOpenButton}
              >
                {hasClosedEndActions ? (
                  <>
                    {!!renderActions && (
                      <SearchCommandActions>
                        {renderActions({
                          onClearSearchValue: handleClearSearchValue,
                          onSetSearchValue: handleSetSearchValue,
                        })}
                      </SearchCommandActions>
                    )}
                    {!!onClear && !!searchValue && <SearchCommandClearButton onClick={onClear} />}
                  </>
                ) : null}
              </SearchCommandTrigger>
            </RadixPopover.Anchor>
          </div>

          <RadixPopover.Portal>
            <RadixPopover.Content
              className={classNames(
                'data-[state=closed]:fade-out data-[state=open]:fade-in relative top-[calc(var(--radix-popover-trigger-height)*-1)] left-0 flex max-h-[calc(var(--radix-popover-content-available-height)-32px)] w-[var(--radix-popover-trigger-width)] flex-col gap-0 rounded-20 transition-all data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:transition-none data-[state=open]:transition-none',
              )}
              data-testid="SEARCH_COMMAND_CONTENT"
              onAnimationEnd={handleSearchCommandContentAnimationEnd}
            >
              <div
                className={classNames(
                  'flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-20 border ads:border-strong border-gray-6 ads:bg-base bg-gray-1 shadow-search-command',
                )}
              >
                {showVisualSearchPanel ? (
                  <SearchCommandVisualSearchPanel
                    onClose={handleCloseVisualSearch}
                    onSearch={handleSimilarImageSearch}
                  />
                ) : (
                  <>
                    <div
                      className={classNames(
                        'relative -mx-px -mt-px flex shrink-0 items-stretch overflow-hidden rounded-t-20',
                        compact ? 'h-8' : 'h-10',
                      )}
                      data-testid="SEARCH_COMMAND_FIELD_CHROME"
                    >
                      <SearchCommandInputBase
                        canUseSmartSearch={canUseSmartSearch}
                        compact={compact}
                        count={filtersCount}
                        onValueChange={onValueChange}
                        onVisualSearchClick={handleVisualSearchClick}
                        placeholder={placeholder}
                        ref={searchInputRef}
                        value={searchValue}
                        visualSearchTooltip={SEARCH_COMMAND_VISUAL_SEARCH_TOOLTIP}
                      >
                        {hasOpenEndActions ? (
                          <>
                            {!!renderActions && (
                              <SearchCommandActions>
                                {renderActions({
                                  onClearSearchValue: handleClearSearchValue,
                                  onSetSearchValue: handleSetSearchValue,
                                })}
                              </SearchCommandActions>
                            )}
                            {!!onClear && <SearchCommandClearButton onClick={onClear} />}
                          </>
                        ) : null}
                      </SearchCommandInputBase>
                    </div>
                    {searchType === 'keyword' && filterBar}
                    {searchType === 'keyword' && (
                      <SearchCommandList
                        hideSuggestions={hideSuggestions}
                        isOpen={isOpen}
                        listStart={renderListStart?.({ onClose: handleCloseSearchCommand })}
                        onBoardClick={onBoardClick}
                        onSavedFilterSelect={onSavedFilterSelect}
                        onSuggestionClick={onSuggestionClick}
                        renderCustomContent={renderCustomContent}
                        searchValue={searchValue}
                        suggestions={suggestions}
                      />
                    )}
                    {searchType === 'smart' && (
                      <SmartSearchCommandList
                        isOpen={isOpen}
                        listStart={renderListStart?.({ onClose: handleCloseSearchCommand })}
                        onSelect={onSuggestionClick}
                      />
                    )}
                  </>
                )}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      </Command>
    );
  },
);

SearchCommand.displayName = 'SearchCommand';
